import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { OverviewData } from '@/client/types/ai-management';

const OVERVIEW = '/llm-admin/overview';

// ─── Query keys ──────────────────────────────────────────────────────────────
// One namespace for the whole LLM-admin surface so mutations can invalidate
// related slices without importing every query module.

export const aiKeys = {
  all: ['llm-admin'] as const,
  overview: (sinceHours: number) => [...aiKeys.all, 'overview', sinceHours] as const,
  usageSummary: (params: object) => [...aiKeys.all, 'usage-summary', params] as const,
  timeseries: (params: object) => [...aiKeys.all, 'timeseries', params] as const,
  errors: (params: object) => [...aiKeys.all, 'errors', params] as const,
  budgets: () => [...aiKeys.all, 'budgets'] as const,
  enablement: () => [...aiKeys.all, 'enablement'] as const,
  backends: () => [...aiKeys.all, 'backends'] as const,
};

/** The `/llm-admin/*` proxy returns `{ status, data }`; unwrap to the payload. */
export const unwrapEnvelope = <T,>(raw: any): T =>
  raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw ? raw.data : raw;

const useOverviewFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (sinceHours: number): Promise<OverviewData> => {
    const raw = await request<any>(`${OVERVIEW}?since_hours=${sinceHours}`);
    return unwrapEnvelope<OverviewData>(raw);
  }, 'Failed to load LLM usage overview');
};

export const useOverviewQuery = (sinceHours: number, enabled = true) => {
  const queryFn = useOverviewFn();
  return useQuery({
    queryKey: aiKeys.overview(sinceHours),
    queryFn: () => queryFn(sinceHours),
    enabled,
    // Usage data moves quickly; keep it fresh on revisit but hold prior data
    // through window changes to avoid layout flashes.
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
